import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';
import EnterpriseUserPasswordResetModal from './EnterpriseUserPasswordResetModal';

const EnterpriseUsers = () => {
  const [users, setEnterpriseUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEnterpriseUser, setEditingEnterpriseUser] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Helper to check if Save should be enabled (for Edit User)
  const isSaveEnabled = () => {
    const basicValidation = formData.name.trim() && formData.email.trim() && emailValidation.isValid && nameValidation.isValid;

    // Same validation for both editing and creating users - password field removed
    return basicValidation;
  };

  // Email validation function
  const validateEmail = (email) => {
    if (!email.trim()) {
      return { isValid: false, message: 'Email is required' };
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    // Domain validation
    const emailDomain = email.toLowerCase().split('@')[1];
    if (!allowedDomains.includes(emailDomain)) {
      return {
        isValid: false,
        message: `Email domain must be one of: ${allowedDomains.join(', ')}`
      };
    }

    return { isValid: true, message: '' };
  };

  // Name validation function
  const validateName = (name) => {
    if (!name.trim()) {
      return { isValid: false, message: 'Name is required' };
    } else if (name.trim().length < 3) {
      return { isValid: false, message: 'Name must be at least 3 characters long' };
    } else if (name.trim().length > 100) {
      return { isValid: false, message: 'Name must be less than 100 characters' };
    }

    return { isValid: true, message: '' };
  };

  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [userCategory, setEnterpriseUserCategory] = useState('customer');
  const [statusFilter, setStatusFilter] = useState(''); // All/Active/Not Active
  const [lockedFilter, setLockedFilter] = useState(''); // All/Locked/Not Locked
  const [passwordChangeFilter, setPasswordChangeFilter] = useState(''); // All/Required/Not Required

  // Password reset modal state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState(null);

  // Password reset confirmation modal state
  const [showPasswordResetConfirmModal, setShowPasswordResetConfirmModal] = useState(false);
  const [passwordResetConfirmData, setPasswordResetConfirmData] = useState(null);

  // Role assignment modal state
  const [showRoleAssignmentModal, setShowRoleAssignmentModal] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [assigningRole, setAssigningRole] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    mobile: '',
    organizationId: '',
    category: 'customer',
    isActive: true,
    isLocked: false,
    isPerson: true
  });

  // Email validation state
  const [emailValidation, setEmailValidation] = useState({
    isValid: true,
    message: ''
  });

  // Name validation state
  const [nameValidation, setNameValidation] = useState({
    isValid: true,
    message: ''
  });

  // Predefined allowed domains
  const allowedDomains = ['caelustech.africa', 'simplify-edms.com'];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadEnterpriseUsers();
  }, [currentPage, search, itemsPerPage, userCategory, statusFilter, lockedFilter, passwordChangeFilter, sortField, sortDirection]);

  const loadInitialData = async () => {
    try {

      // Load available enterprise roles for role assignment
      const rolesResponse = await apiService.getEnterpriseRoles(1, 100); // Get all roles
      if (rolesResponse.success) {
        setAvailableRoles(rolesResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const loadEnterpriseUsers = async () => {
    setLoading(true);
    setError('');
    console.log('Loading users with params:', {
      userCategory, currentPage, itemsPerPage, search, statusFilter, lockedFilter, passwordChangeFilter
    });
    try {
      const response = await apiService.getEnterpriseUsers(userCategory, currentPage, itemsPerPage, search);
      if (response.success) {
        // Ensure users is always an array
        let userData = response.data || [];
        console.log('Users loaded:', userData);

        // Apply status filter
        if (statusFilter === 'active') {
          userData = userData.filter(user => user.is_active === true);
        } else if (statusFilter === 'inactive') {
          userData = userData.filter(user => user.is_active === false);
        }

        // Apply locked filter
        if (lockedFilter === 'locked') {
          userData = userData.filter(user => user.is_locked === true);
        } else if (lockedFilter === 'unlocked') {
          userData = userData.filter(user => user.is_locked !== true);
        }

        // Apply password change filter
        if (passwordChangeFilter === 'required') {
          userData = userData.filter(user => user.password_change_required === true);
        } else if (passwordChangeFilter === 'not_required') {
          userData = userData.filter(user => user.password_change_required !== true);
        }

        // Apply sorting before setting users
        const sortedData = sortData(Array.isArray(userData) ? userData : []);
        setEnterpriseUsers(sortedData);
        // Update total pages based on filtered results if any filter is applied
        const hasFilters = statusFilter || lockedFilter || passwordChangeFilter;
        const totalCount = hasFilters ? sortedData.length : (response.pagination?.total || 0);
        setTotalPages(Math.ceil(totalCount / itemsPerPage));
      } else {
        setError(response.error || 'Failed to load users');
        setEnterpriseUsers([]); // Reset to empty array on error
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
      setEnterpriseUsers([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      setCurrentPage(1);
      setSearch(e.target.value);
    }
  };

  const handleCategoryChange = (category) => {
    setEnterpriseUserCategory(category);
    setCurrentPage(1);
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setEditingEnterpriseUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        category: user.is_person ? 'customer' : 'enterprise',
        isActive: user.is_active !== false,
        isLocked: user.is_locked === true,
        isPerson: user.is_person !== false
      });
      // Validate existing name and email when editing
      const nameValidation = validateName(user.name || '');
      setNameValidation(nameValidation);
      const emailValidation = validateEmail(user.email || '');
      setEmailValidation(emailValidation);
    } else {
      setEditingEnterpriseUser(null);
      setFormData({
        name: '',
        email: '',
        category: 'customer',
        isActive: true,
        isLocked: false,
        isPerson: true
      });
      // Reset validation for new user
      setNameValidation({ isValid: true, message: '' });
      setEmailValidation({ isValid: true, message: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEnterpriseUser(null);
    setFormData({
      name: '',
      email: '',
      category: 'customer',
      isActive: true,
      isLocked: false,
      isPerson: true
    });
    // Reset validation
    setNameValidation({ isValid: true, message: '' });
    setEmailValidation({ isValid: true, message: '' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Validate email field in real-time
    if (name === 'email') {
      const validation = validateEmail(value);
      setEmailValidation(validation);
    }

    // Validate name field in real-time
    if (name === 'name') {
      const validation = validateName(value);
      setNameValidation(validation);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate name before saving
    const nameValidationResult = validateName(formData.name);
    setNameValidation(nameValidationResult);

    // Validate email before saving
    const emailValidationResult = validateEmail(formData.email);
    setEmailValidation(emailValidationResult);

    if (!nameValidationResult.isValid || !emailValidationResult.isValid) {
      setError('Please fix validation errors before saving.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        is_person: formData.isPerson
      };

      userData.email = formData.email.trim();

      let response;

      if (editingEnterpriseUser) {
        // Update existing user
        response = await apiService.updateEnterpriseUser(editingEnterpriseUser.id, userData);
      } else {
        // Create new user - password will be handled through password reset
        response = await apiService.createEnterpriseUser(userData);
      }


      if (response.success) {
        handleCloseModal();
        await loadEnterpriseUsers();
      } else {
        setError(response.error || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id, isActive) => {
    try {
      const response = await apiService.toggleEnterpriseUserStatus(id, isActive);
      if (response.success) {
        await loadEnterpriseUsers();
      } else {
        setError(response.error || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('Failed to update user status');
    }
  };

  const handleLockUser = async (user) => {
    const action = user.is_locked ? 'unlock' : 'lock';
    const confirmMessage = `Are you sure you want to ${action} user "${user.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser || !currentUser.id) {
        setError('Unable to determine current user');
        return;
      }

      const response = await apiService.put(`/user_account/lock/${user.id}`, {
        is_locked: !user.is_locked,
        last_updated_by: currentUser.id
      });

      if (response.success) {
        await loadEnterpriseUsers();
      } else {
        setError(response.error || 'Failed to update user lock status');
      }
    } catch (error) {
      console.error('Error locking/unlocking user:', error);
      setError('Failed to update user lock status');
    }
  };

  const handleArchiveUser = async (user) => {
    if (!window.confirm(`Are you sure you want to archive user "${user.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser || !currentUser.id) {
        setError('Unable to determine current user');
        return;
      }

      const response = await apiService.makeRequest(`/user_account/${user.id}`, {
        method: 'DELETE',
        body: JSON.stringify({
          last_updated_by: currentUser.id
        })
      });

      if (response.success) {
        await loadEnterpriseUsers();
      } else {
        setError(response.error || 'Failed to archive user');
      }
    } catch (error) {
      console.error('Error archiving user:', error);
      setError('Failed to archive user');
    }
  };

  // Password reset confirmation handlers
  const handlePasswordResetConfirmation = (user_account, requirePasswordChange) => {
    performPasswordReset(user_account, requirePasswordChange);
    setShowPasswordResetConfirmModal(false);
    setPasswordResetConfirmData(null);
  };

  const handlePasswordResetCancel = () => {
    setShowPasswordResetConfirmModal(false);
    setPasswordResetConfirmData(null);
  };

  // Reset password for enterprise user
  const resetPassword = async (user_account) => {
    const confirmMessage = `Are you sure you want to reset the password for ${user_account.name}? A new temporary password will be generated.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Show modal to ask if the user should be required to change password at first login
    setPasswordResetConfirmData(user_account);
    setShowPasswordResetConfirmModal(true);
  };

  // Perform the actual password reset
  const performPasswordReset = async (user_account, requirePasswordChange) => {
    try {
      const payload = {
        user_account_id: user_account.id,
        password_change_required: requirePasswordChange
      };
      console.log('Resetting password using payload:', payload);
      const response = await apiService.post('/user_account_password/reset', payload);
      console.log('Reset outcome', response);

      // Show the new reset password to the admin
      if (response.data && response.data.reset_password) {
        setPasswordResetData({
          userAccount: user_account,
          resetPassword: response.data.reset_password
        });
        setShowPasswordResetModal(true);
      } else {
        const changeRequiredText = requirePasswordChange
          ? "The user will be required to change their password at first login."
          : "The user can use the new password as-is.";
        alert(`Password has been reset successfully! A new temporary password has been generated for ${user_account.name}. ${changeRequiredText}`);
      }

      // Refresh the table after successful password reset
      await loadEnterpriseUsers();
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password. Please try again.');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Role assignment functions
  const handleShowRoleAssignmentModal = (user) => {
    setSelectedUserForRole(user);
    setSelectedRoleId('');
    setShowRoleAssignmentModal(true);
  };

  const handleCloseRoleAssignmentModal = () => {
    setShowRoleAssignmentModal(false);
    setSelectedUserForRole(null);
    setSelectedRoleId('');
  };

  const handleAssignRole = async () => {
    if (!selectedRoleId || !selectedUserForRole) {
      setError('Please select a role to assign');
      return;
    }

    setAssigningRole(true);
    setError('');

    try {
      const response = await apiService.assignRoleToUser(selectedUserForRole.id, selectedRoleId);

      if (response.success) {
        handleCloseRoleAssignmentModal();
        await loadEnterpriseUsers(); // Refresh the user list to show updated roles
      } else {
        setError(response.error || 'Failed to assign role');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      setError('Failed to assign role');
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveRole = async (user, roleId, roleName) => {
    const confirmMessage = `Are you sure you want to remove the "${roleName}" role from user "${user.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError('');
      const response = await apiService.removeRoleFromUser(user.id, roleId);

      if (response.success) {
        await loadEnterpriseUsers(); // Refresh the user list to show updated roles
      } else {
        setError(response.error || 'Failed to remove role');
      }
    } catch (error) {
      console.error('Error removing role:', error);
      setError('Failed to remove role');
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle special cases
      if (sortField === 'roles') {
        aVal = Array.isArray(a.roles) ? a.roles.join(', ') : (a.roles || '');
        bVal = Array.isArray(b.roles) ? b.roles.join(', ') : (b.roles || '');
      } else if (sortField === 'created_at' || sortField === 'last_updated_at' || sortField === 'last_login') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortField === 'is_active') {
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
      } else {
        // Convert to string for consistent comparison
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
    }
    return sortDirection === 'asc' ?
      <i className="bi bi-arrow-up ms-1 text-primary"></i> :
      <i className="bi bi-arrow-down ms-1 text-primary"></i>;
  };

  const isCustomerEnterpriseUser = true;

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-people me-2"></i>
              Enterprise Users
            </h2>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-person-plus me-2"></i>
              Add Enterprise User
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search and Filter */}
      <Card className="mb-4" style={{ border: '1px solid #282425' }}>
        <Card.Header style={{
          backgroundColor: '#282425',
          color: '#fcfcfc',
          borderBottom: '2px solid #662628'
        }}>
          <h6 className="mb-0">
            <i className="bi bi-funnel me-2" style={{ color: '#e42b2e' }}></i>
            Search & Filter Options
          </h6>
        </Card.Header>
        <Card.Body style={{ backgroundColor: '#fcfcfc' }}>
          {/* First Row - Search and Items Per Page */}
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-search me-2" style={{ color: '#662628' }}></i>
                  Search Enterprise Users
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email..."
                  onKeyDown={handleSearch}
                  style={{
                    borderColor: '#662628',
                    focusBorderColor: '#e42b2e',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => {
                    handleSearch(e);
                    e.target.style.borderColor = '#662628';
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-list-ol me-2" style={{ color: '#662628' }}></i>
                  Per Page
                </Form.Label>
                <Form.Select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Second Row - Status Filters */}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-check-circle me-2" style={{ color: '#662628' }}></i>
                  Status Filter
                </Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Not Active Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-lock me-2" style={{ color: '#662628' }}></i>
                  Lock Status
                </Form.Label>
                <Form.Select
                  value={lockedFilter}
                  onChange={(e) => setLockedFilter(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All</option>
                  <option value="locked">Locked Only</option>
                  <option value="unlocked">Not Locked Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-key me-2" style={{ color: '#662628' }}></i>
                  Password Change
                </Form.Label>
                <Form.Select
                  value={passwordChangeFilter}
                  onChange={(e) => setPasswordChangeFilter(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All</option>
                  <option value="required">Password Change Required</option>
                  <option value="not_required">No Password Change Required</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : !Array.isArray(users) || users.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-people display-1"></i>
                  <div className="mt-2">No users found</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('user_account_id')}
                      >
                        User Account ID {getSortIcon('user_account_id')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Name {getSortIcon('name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('email')}
                      >
                        Email {getSortIcon('email')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('is_person')}
                      >
                        Type {getSortIcon('is_person')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('is_active')}
                      >
                        Status {getSortIcon('is_active')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('last_login')}
                      >
                        Last Login {getSortIcon('last_login')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('roles')}
                      >
                        Roles {getSortIcon('roles')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('created_at')}
                      >
                        Created {getSortIcon('created_at')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('last_updated_at')}
                      >
                        Last Updated {getSortIcon('last_updated_at')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(users) && users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.user_account_id}</td>
                        <td>
                          <div className="fw-medium">{user.name}</div>
                        </td>
                        <td>
                          {user.email
                          }
                        </td>
                        <td>
                          {(
                            <Badge bg="info">
                              {user.is_person ? 'Person' : 'System'}
                            </Badge>
                          )
                          }
                        </td>
                        <td>
                          <div>
                            <Badge bg={user.is_active ? 'success' : 'secondary'} className="me-1">
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {user.is_locked && <Badge bg="danger" className="me-1">Locked</Badge>}
                            {user.password_change_required && <Badge bg="warning" className="me-1">Password Change Pending</Badge>}
                          </div>
                        </td>
                        <td>{(user.last_login) ? apiService.formatDate(user.last_login) : 'N/A'}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {/* {console.log('Rendering roles for user:', user.roles)} */}
                            {/* {console.log('Rendering role ids for user:', user.role_id_list)} */}
                            {Array.isArray(user.roles) && user.roles.length > 0
                              ? user.roles.map((role, index) => {
                                // Get the corresponding role ID from role_id_list
                                const roleId = Array.isArray(user.role_id_list) && user.role_id_list[index]
                                  ? user.role_id_list[index]
                                  : (typeof role === 'string' ? role : role.id);
                                const roleName = typeof role === 'string' ? role : role.name;

                                return (
                                  <Badge
                                    key={index}
                                    bg="primary"
                                    className="d-flex align-items-center gap-1"
                                    style={{ fontSize: '0.75rem' }}
                                  >
                                    <span>{roleName}</span>
                                    <i
                                      className="bi bi-x-circle-fill"
                                      style={{
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        color: 'rgba(255,255,255,0.8)'
                                      }}
                                      onClick={() => handleRemoveRole(
                                        user,
                                        roleId,
                                        roleName
                                      )}
                                      title={`Remove ${roleName} role`}
                                      onMouseEnter={(e) => e.target.style.color = 'white'}
                                      onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.8)'}
                                    ></i>
                                  </Badge>
                                );
                              })
                              : <span className="text-muted">No roles assigned</span>
                            }
                          </div>
                        </td>
                        <td>{apiService.formatDate(user.created_at)}</td>
                        <td>{apiService.formatDate(user.last_updated_at)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            {user.is_active ? (
                              <>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleShowModal(user)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleShowRoleAssignmentModal(user)}
                                  title="Assign Role"
                                >
                                  <i className="bi bi-person-badge"></i> Assign Role
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => resetPassword(user)}
                                  title="Reset Password"
                                >
                                  <i className="bi bi-key"></i> Reset Password
                                </Button>
                                {user.is_locked ? (
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleLockUser(user)}
                                    title="Unlock User"
                                  >
                                    <i className="bi bi-unlock"></i> Unlock User
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleLockUser(user)}
                                    title="Lock User"
                                  >
                                    <i className="bi bi-lock"></i> Lock User
                                  </Button>
                                )}
                                <Button
                                  variant="outline-dark"
                                  size="sm"
                                  onClick={() => handleArchiveUser(user)}
                                  title="Archive User"
                                >
                                  <i className="bi bi-archive"></i> Archive User
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleToggleStatus(user.id, true)}
                                title="Reactivate User"
                              >
                                <i className="bi bi-arrow-clockwise"></i> Reactivate User
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, Array.isArray(users) ? users.length : 0)} of {Array.isArray(users) ? users.length : 0} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEnterpriseUser ? 'Edit Enterprise User' : 'Add Enterprise User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>

            {/* Improved form layout with better field arrangement */}
            {/* Row 1: Full Name */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                    isInvalid={!nameValidation.isValid && formData.name.length > 0}
                    isValid={nameValidation.isValid && formData.name.length > 0}
                  />
                  <Form.Control.Feedback type="invalid">
                    {nameValidation.message}
                  </Form.Control.Feedback>
                  <Form.Control.Feedback type="valid">
                    Name looks good!
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Name must be between 3 and 100 characters long
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Row 2: Email */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter email address"
                    isInvalid={!emailValidation.isValid && formData.email.length > 0}
                    isValid={emailValidation.isValid && formData.email.length > 0}
                  />
                  <Form.Control.Feedback type="invalid">
                    {emailValidation.message}
                  </Form.Control.Feedback>
                  <Form.Control.Feedback type="valid">
                    Email format and domain are valid!
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Email must be from one of these domains: {allowedDomains.join(', ')}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Row 3: Password Management Info (for new users) */}
            {!editingEnterpriseUser && (
              <Row>
                <Col md={12}>
                  <Alert variant="info" className="mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Password Management:</strong> After creating the user, use the "Reset Password" action to set up their initial password. The user can be required to change it on first login for security.
                  </Alert>
                </Col>
              </Row>
            )}

            {/* Row 4: User Type */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="isPerson"
                    id="isPerson"
                    label="Is Person"
                    checked={formData.isPerson}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Check this box if this user represents a person (individual user), leave unchecked for enterprisesystem accounts
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving || !isSaveEnabled()}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Enterprise User'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <EnterpriseUserPasswordResetModal
        show={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
        userAccount={passwordResetData?.userAccount}
        resetPassword={passwordResetData?.resetPassword}
      />

      {/* Role Assignment Modal */}
      <Modal show={showRoleAssignmentModal} onHide={handleCloseRoleAssignmentModal} size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-badge me-2"></i>
            Assign Role to {selectedUserForRole?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForRole && (
            <div className="mb-3">
              <div className="alert alert-info">
                <strong>User:</strong> {selectedUserForRole.name} ({selectedUserForRole.email})
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Select Role to Assign *</Form.Label>
            <Form.Select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              required
            >
              <option value="">-- Select a Role --</option>
              {availableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name} ({role.code})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Choose a role to assign to this user. The user will gain the permissions associated with this role.
            </Form.Text>
          </Form.Group>

          {availableRoles.length === 0 && (
            <Alert variant="warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              No roles are available. Please create roles first in the Enterprise Roles section.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRoleAssignmentModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignRole}
            disabled={assigningRole || !selectedRoleId || availableRoles.length === 0}
          >
            {assigningRole ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                Assigning Role...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Assign Role
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password Reset Confirmation Modal */}
      <Modal show={showPasswordResetConfirmModal} onHide={handlePasswordResetCancel} size="md" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
          <Modal.Title>
            <i className="bi bi-key-fill me-2 text-warning"></i>
            Password Reset Options
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#ffffff' }}>
          {passwordResetConfirmData && (
            <div className="text-center">
              <div className="mb-4">
                <i className="bi bi-person-circle display-4 text-primary"></i>
              </div>
              <h5 className="mb-3">Password Change Requirement</h5>
              <p className="mb-4">
                Should <strong>{passwordResetConfirmData.name}</strong> be required to change their password at first login?
              </p>
              <Alert variant="info" className="mb-4">
                <div className="d-flex align-items-start">
                  <i className="bi bi-info-circle me-2 mt-1"></i>
                  <div>
                    <strong>Choose an option:</strong>
                    <ul className="mt-2 mb-0 text-start">
                      <li><strong>Yes:</strong> User must change password on first login (recommended for security)</li>
                      <li><strong>No:</strong> User can use the new temporary password as-is</li>
                    </ul>
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
          <div className="w-100 d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              onClick={() => handlePasswordResetConfirmation(passwordResetConfirmData, false)}
              size="lg"
              style={{ minWidth: '140px' }}
            >
              <i className="bi bi-x-circle me-2"></i>
              No, Allow As-Is
            </Button>
            <Button
              variant="warning"
              onClick={() => handlePasswordResetConfirmation(passwordResetConfirmData, true)}
              size="lg"
              style={{ minWidth: '140px' }}
            >
              <i className="bi bi-check-circle me-2"></i>
              Yes, Require Change
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EnterpriseUsers;
